import { useEffect, useState, useCallback, Fragment } from "react";
import axios from "axios";
import { toast } from "sonner";
import { errMsg } from "./apiError";
import { Trash2, BarChart3, X, CheckCircle2, Mail, Pencil, Receipt, Archive, RotateCcw, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUTS = ["en_attente", "assigné", "approuvé", "refusé"];
const POSTES = ["Gardien", "Défenseur central", "Latéral droit", "Latéral gauche", "Milieu défensif", "Milieu central", "Milieu offensif", "Ailier droit", "Ailier gauche", "Attaquant"];
const MAILLOTS = ["S", "M", "L", "XL", "XXL"];
const MONTANTS = [["20", "20 $US"], ["15", "15 $US"], ["10", "10 $US"], ["5", "5 $US"], ["0", "Gratuit"]];
const editCls = "bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059] w-full";

export const montantLabel = (m) => (m === 0 ? "Gratuit" : `${m ?? 20} $US`);

function EditRegModal({ reg, onSaved, onClose }) {
  const [form, setForm] = useState({
    prenom: reg.prenom || "", nom: reg.nom || "", email: reg.email || "", telephone: reg.telephone || "",
    date_naissance: reg.date_naissance || "", poste_principal: reg.poste_principal || "",
    poste_secondaire: reg.poste_secondaire || "", club_actuel: reg.club_actuel || "",
    taille_maillot: reg.taille_maillot || "M", montant_frais: String(reg.montant_frais ?? 20),
    genre: reg.genre || "masculin",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/registrations/${reg.id}`, {
        prenom: form.prenom, nom: form.nom, email: form.email, telephone: form.telephone,
        date_naissance: form.date_naissance, poste_principal: form.poste_principal,
        poste_secondaire: form.poste_secondaire || null, club_actuel: form.club_actuel || null,
        taille_maillot: form.taille_maillot, montant_frais: parseInt(form.montant_frais, 10), genre: form.genre,
      }, { withCredentials: true });
      toast.success("Inscription mise à jour");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Erreur lors de la mise à jour"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid={`edit-modal-${reg.id.slice(0, 8)}`}>
      <div className="bg-[#121214] border border-[#C5A059]/40 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059]">Modifier l'inscription — {reg.reference}</p>
          <button onClick={onClose} className="text-[#71717A] hover:text-white transition-colors" aria-label="Fermer"><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Prénom</label>
            <input value={form.prenom} onChange={set("prenom")} data-testid="edit-prenom" className={`${editCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Nom</label>
            <input value={form.nom} onChange={set("nom")} data-testid="edit-nom" className={`${editCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Email</label>
            <input type="email" value={form.email} onChange={set("email")} data-testid="edit-email" className={`${editCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Téléphone</label>
            <input value={form.telephone} onChange={set("telephone")} data-testid="edit-telephone" className={`${editCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Date de naissance</label>
            <input type="date" value={form.date_naissance} onChange={set("date_naissance")} data-testid="edit-date-naissance" className={`${editCls} mt-1 [color-scheme:dark]`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Genre</label>
            <select value={form.genre} onChange={set("genre")} data-testid="edit-genre" className={`${editCls} mt-1`}>
              <option value="masculin">Masculin</option>
              <option value="féminin">Féminin</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Poste principal</label>
            <select value={form.poste_principal} onChange={set("poste_principal")} data-testid="edit-poste" className={`${editCls} mt-1`}>
              {POSTES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Poste secondaire</label>
            <select value={form.poste_secondaire} onChange={set("poste_secondaire")} data-testid="edit-poste2" className={`${editCls} mt-1`}>
              <option value="">—</option>
              {POSTES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Club actuel</label>
            <input value={form.club_actuel} onChange={set("club_actuel")} data-testid="edit-club" className={`${editCls} mt-1`} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Taille maillot</label>
            <select value={form.taille_maillot} onChange={set("taille_maillot")} data-testid="edit-maillot" className={`${editCls} mt-1`}>
              {MAILLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Frais d'inscription</label>
            <select value={form.montant_frais} onChange={set("montant_frais")} data-testid="edit-montant" className={`${editCls} mt-1`}>
              {MONTANTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-8">
          <button onClick={save} disabled={saving} data-testid="edit-save"
            className="bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
          <button onClick={onClose} className="text-xs uppercase tracking-widest text-[#71717A] hover:text-white transition-colors">Annuler</button>
        </div>
      </div>
    </div>
  );
}

function StatsEditor({ reg, onSaved, onClose }) {
  const [stats, setStats] = useState({ buts: reg.stats?.buts ?? 0, passes: reg.stats?.passes ?? 0, matchs: reg.stats?.matchs ?? 0 });
  const [veo, setVeo] = useState({
    distance_km: reg.stats?.distance_km ?? "", vitesse_max: reg.stats?.vitesse_max ?? "", sprints: reg.stats?.sprints ?? "",
    passes_pct: reg.stats?.passes_pct ?? "", tirs_cadres: reg.stats?.tirs_cadres ?? "", duels: reg.stats?.duels ?? "",
  });
  const [note, setNote] = useState(reg.note_coach || "");
  const [genre, setGenre] = useState(reg.genre || "masculin");

  const save = async () => {
    const veoClean = Object.fromEntries(Object.entries(veo).filter(([, v]) => v !== "").map(([k, v]) => [k, +v]));
    await axios.patch(`${API}/admin/registrations/${reg.id}`, {
      stats: { buts: +stats.buts, passes: +stats.passes, matchs: +stats.matchs, ...veoClean },
      note_coach: note || null,
      genre,
    }, { withCredentials: true });
    toast.success("Statistiques enregistrées — le joueur est notifié (email + push)");
    onSaved();
  };

  return (
    <tr className="border-t border-[#27272A] bg-[#121214]">
      <td colSpan={10} className="px-4 py-6">
        <div className="flex flex-wrap items-end gap-6">
          {[["buts", "Buts"], ["passes", "Passes"], ["matchs", "Matchs"]].map(([k, label]) => (
            <div key={k}>
              <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{label}</label>
              <input
                type="number" min="0" value={stats[k]}
                onChange={(e) => setStats({ ...stats, [k]: e.target.value })}
                data-testid={`stats-input-${k}-${reg.id.slice(0, 8)}`}
                className="block w-24 bg-[#0A0A0C] border border-[#27272A] px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#C5A059]"
              />
            </div>
          ))}
          <div className="flex-1 min-w-[240px]">
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Note du coach</label>
            <input
              type="text" value={note} onChange={(e) => setNote(e.target.value)}
              placeholder="Ex: Excellent placement, à suivre de près…"
              data-testid={`stats-note-${reg.id.slice(0, 8)}`}
              className="block w-full bg-[#0A0A0C] border border-[#27272A] px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#C5A059]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#71717A]">Genre</label>
            <select
              value={genre} onChange={(e) => setGenre(e.target.value)}
              data-testid={`stats-genre-${reg.id.slice(0, 8)}`}
              className="block w-36 bg-[#0A0A0C] border border-[#27272A] px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#C5A059]"
            >
              <option value="masculin">Masculin</option>
              <option value="féminin">Féminin</option>
            </select>
          </div>
          <button onClick={save} data-testid={`stats-save-${reg.id.slice(0, 8)}`} className="bg-[#C5A059] text-black px-6 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors">
            Enregistrer
          </button>
          <button onClick={onClose} className="text-[#71717A] hover:text-white transition-colors p-2" aria-label="Fermer"><X size={16} /></button>
        </div>
        <div className="mt-6 border-t border-[#27272A] pt-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-4">Données caméra Veo (visibles dans le portail joueur)</p>
          <div className="flex flex-wrap gap-4">
            {[["distance_km", "Distance (km)"], ["vitesse_max", "Vitesse max (km/h)"], ["sprints", "Sprints"], ["passes_pct", "Passes réussies (%)"], ["tirs_cadres", "Tirs cadrés"], ["duels", "Duels gagnés"]].map(([k, label]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-widest text-[#71717A]">{label}</label>
                <input
                  type="number" min="0" step="any" value={veo[k]}
                  onChange={(e) => setVeo({ ...veo, [k]: e.target.value })}
                  data-testid={`stats-input-${k}-${reg.id.slice(0, 8)}`}
                  className="block w-32 bg-[#0A0A0C] border border-[#27272A] px-3 py-2 mt-1 text-sm focus:outline-none focus:border-[#C5A059]"
                />
              </div>
            ))}
          </div>
        </div>
      </td>
    </tr>
  );
}

export default function AdminRegistrations() {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [archives, setArchives] = useState(null);
  const [showArchives, setShowArchives] = useState(false);
  const [search, setSearch] = useState("");
  const [archiveSearch, setArchiveSearch] = useState("");

  const load = useCallback(() => {
    axios.get(`${API}/admin/registrations`, { withCredentials: true })
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erreur de chargement des inscriptions"));
  }, []);
  useEffect(load, [load]);

  const loadArchives = useCallback(() => {
    axios.get(`${API}/admin/registrations/archives`, { params: archiveSearch ? { q: archiveSearch } : {}, withCredentials: true })
      .then((r) => setArchives(r.data))
      .catch(() => setArchives([]));
  }, [archiveSearch]);

  useEffect(() => { if (showArchives) loadArchives(); }, [showArchives, loadArchives]);

  const filtered = items.filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return `${r.prenom} ${r.nom} ${r.reference || ""} ${r.email || ""} ${r.telephone || ""}`.toLowerCase().includes(q);
  });

  const setStatut = async (id, statut) => {
    await axios.patch(`${API}/admin/registrations/${id}`, { statut }, { withCredentials: true });
    toast.success("Statut mis à jour");
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette inscription ? Elle sera conservée dans les archives.")) return;
    await axios.delete(`${API}/admin/registrations/${id}`, { withCredentials: true });
    toast.success("Inscription supprimée et archivée");
    load();
    if (showArchives) loadArchives();
  };

  const restaurer = async (id) => {
    try {
      await axios.post(`${API}/admin/registrations/archives/${id}/restaurer`, {}, { withCredentials: true });
      toast.success("Inscription restaurée");
      load();
      loadArchives();
    } catch (e) {
      toast.error(errMsg(e, "Restauration impossible"));
    }
  };

  const emettreFacture = async (r) => {
    try {
      const { data } = await axios.post(`${API}/admin/registrations/${r.id}/facture`, {}, { withCredentials: true });
      toast.success(`Facture ${data.numero} ${data.deja_existante ? "déjà émise" : "émise"} — ${data.montant}`, {
        description: data.envoye_email ? `Envoyée à ${data.email}` : "Email non délivré — ouvrez le PDF depuis l'onglet Factures",
      });
    } catch (e) {
      toast.error(errMsg(e, "Erreur lors de l'émission"));
    }
  };

  const markPaid = async (id) => {
    await axios.patch(`${API}/admin/registrations/${id}`, { paiement_statut: "payée" }, { withCredentials: true });
    toast.success("Paiement validé — équipe assignée et email envoyé");
    load();
  };

  const sendReminder = async (id) => {
    try {
      await axios.post(`${API}/admin/registrations/${id}/rappel`, {}, { withCredentials: true });
      toast.success("Email de rappel envoyé");
    } catch (e) {
      toast.error(errMsg(e, "Erreur lors de l'envoi"));
    }
  };

  return (
    <div data-testid="admin-registrations">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <p className="text-sm text-[#71717A]">{filtered.length} inscription(s){search ? ` sur ${items.length}` : ""}</p>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (nom, référence, email, téléphone)…"
            data-testid="registrations-search"
            className="bg-[#0A0A0C] border border-[#27272A] pl-9 pr-3 py-2 text-xs w-80 focus:outline-none focus:border-[#C5A059]"
          />
        </div>
      </div>
      <div className="overflow-x-auto border border-[#27272A]">
        <table className="w-full text-sm min-w-[1000px]">
          <thead>
            <tr className="bg-[#121214] text-left text-[10px] uppercase tracking-widest text-[#71717A]">
              {["Joueur", "Poste", "Naissance", "Club", "Contact", "Équipe", "Paiement", "Détails", "Statut", ""].map((h) => (
                <th key={h} className="px-4 py-3 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <Fragment key={r.id}>
                <tr data-testid={`registration-row-${r.id.slice(0, 8)}`} className="border-t border-[#27272A] hover:bg-[#121214] transition-colors">
                  <td className="px-4 py-3 font-medium">{r.prenom} {r.nom} <span className={`ml-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 border ${r.genre === "féminin" ? "border-pink-400/50 text-pink-300" : "border-sky-400/50 text-sky-300"}`} data-testid={`registration-genre-${r.id.slice(0, 8)}`}>{r.genre === "féminin" ? "F" : "M"}</span></td>
                  <td className="px-4 py-3 text-[#A1A1AA]">{r.poste_principal}</td>
                  <td className="px-4 py-3 text-[#A1A1AA]">{r.date_naissance}</td>
                  <td className="px-4 py-3 text-[#A1A1AA]">{r.club_actuel || "—"}</td>
                  <td className="px-4 py-3 text-[#A1A1AA]">{r.email}<br />{r.telephone}</td>
                  <td className="px-4 py-3 text-[#C5A059]">{r.equipe || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 border ${r.paiement_statut === "payée" ? "border-[#C5A059]/50 text-[#C5A059]" : "border-[#27272A] text-[#71717A]"}`}>
                      {r.paiement_statut === "payée" ? "Payée" : r.paiement_statut === "en_attente_paiement" ? "En attente" : "—"}
                    </span>
                    <span className="block text-[11px] text-[#A1A1AA] mt-1" data-testid={`registration-montant-${r.id.slice(0, 8)}`}>{montantLabel(r.montant_frais)}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-[#A1A1AA]">
                    {r.taille_maillot && <span className="block">Maillot <strong className="text-[#C5A059]">{r.taille_maillot}</strong></span>}
                    {r.methode_paiement === "bureau_manuel" && (
                      <span className="block" data-testid={`registration-manual-${r.id.slice(0, 8)}`}>Bureau{r.cree_par ? <> — par <strong className="text-white">{r.cree_par}</strong></> : ""}</span>
                    )}
                    {r.rdv_date && <span className="block text-[#C5A059]">RDV {r.rdv_date} · {r.rdv_heure}</span>}
                    {r.preuve_paiement && (
                      <a href={`${API}/admin/registrations/${r.id}/preuve`} target="_blank" rel="noreferrer" data-testid={`registration-proof-${r.id.slice(0, 8)}`} className="text-[#C5A059] hover:underline">
                        Voir la preuve
                      </a>
                    )}
                    {!r.rdv_date && !r.preuve_paiement && r.methode_paiement !== "bureau_manuel" && "—"}
                  </td>
                  <td className="px-4 py-3">
                    <select
                      value={r.statut}
                      onChange={(e) => setStatut(r.id, e.target.value)}
                      data-testid={`registration-status-${r.id.slice(0, 8)}`}
                      className="bg-[#0A0A0C] border border-[#27272A] text-xs px-2 py-1.5 focus:outline-none focus:border-[#C5A059]"
                    >
                      {STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {r.paiement_statut === "en_attente_paiement" && (
                        <>
                          <button
                            onClick={() => markPaid(r.id)}
                            data-testid={`registration-mark-paid-${r.id.slice(0, 8)}`}
                            className="text-[#71717A] hover:text-[#C5A059] transition-colors"
                            aria-label="Marquer payée"
                            title="Marquer payée (Mobile Money reçu)"
                          >
                            <CheckCircle2 size={15} />
                          </button>
                          <button
                            onClick={() => sendReminder(r.id)}
                            data-testid={`registration-reminder-${r.id.slice(0, 8)}`}
                            className="text-[#71717A] hover:text-[#C5A059] transition-colors"
                            aria-label="Envoyer un rappel"
                            title="Envoyer un rappel de paiement"
                          >
                            <Mail size={15} />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => setEditing(r)}
                        data-testid={`registration-edit-${r.id.slice(0, 8)}`}
                        className="text-[#71717A] hover:text-[#C5A059] transition-colors"
                        aria-label="Modifier"
                        title="Modifier les informations (nom, contact, frais…)"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => emettreFacture(r)}
                        data-testid={`registration-facture-${r.id.slice(0, 8)}`}
                        className="text-[#71717A] hover:text-[#C5A059] transition-colors"
                        aria-label="Facture"
                        title="Émettre la facture & contrat"
                      >
                        <Receipt size={15} />
                      </button>
                      <button
                        onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                        data-testid={`registration-stats-${r.id.slice(0, 8)}`}
                        className={`transition-colors ${expanded === r.id ? "text-[#C5A059]" : "text-[#71717A] hover:text-[#C5A059]"}`}
                        aria-label="Stats"
                        title="Stats & note coach"
                      >
                        <BarChart3 size={15} />
                      </button>
                      <button onClick={() => remove(r.id)} data-testid={`registration-delete-${r.id.slice(0, 8)}`} className="text-[#71717A] hover:text-red-400 transition-colors" aria-label="Supprimer">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
                {expanded === r.id && (
                  <StatsEditor reg={r} onSaved={() => { setExpanded(null); load(); }} onClose={() => setExpanded(null)} />
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <p className="text-center text-[#71717A] text-sm py-12">Aucune inscription pour le moment.</p>}
      </div>

      {editing && <EditRegModal reg={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}

      {/* Archives des inscriptions supprimées */}
      <div className="mt-10">
        <button
          onClick={() => setShowArchives(!showArchives)}
          data-testid="archives-toggle"
          className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#71717A] hover:text-[#C5A059] transition-colors"
        >
          <Archive size={14} /> Archives — inscriptions supprimées {showArchives ? "▲" : "▼"}
        </button>
        {showArchives && (
          <div className="mt-4 border border-[#27272A]" data-testid="archives-panel">
            <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-[#27272A] bg-[#121214]">
              <div className="relative flex-1 max-w-sm">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
                <input
                  value={archiveSearch}
                  onChange={(e) => setArchiveSearch(e.target.value)}
                  placeholder="Rechercher dans la corbeille…"
                  data-testid="archives-search"
                  className="w-full bg-[#0A0A0C] border border-[#27272A] pl-9 pr-3 py-2 text-xs focus:outline-none focus:border-[#C5A059]"
                />
              </div>
              {archives && <p className="text-xs text-[#71717A]">{archives.length} archivée(s)</p>}
            </div>
            {!archives ? (
              <p className="px-5 py-8 text-center text-[#71717A] text-sm">Chargement…</p>
            ) : archives.length === 0 ? (
              <p className="px-5 py-8 text-center text-[#71717A] text-sm">Aucune inscription archivée.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#121214] text-left text-[10px] uppercase tracking-widest text-[#71717A]">
                    {["Joueur", "Référence", "Contact", "Montant", "Supprimée le", "Par", ""].map((h) => (
                      <th key={h} className="px-4 py-3 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {archives.map((a) => (
                    <tr key={a.id} data-testid={`archive-row-${a.id.slice(0, 8)}`} className="border-t border-[#27272A]">
                      <td className="px-4 py-3 font-medium">{a.prenom} {a.nom}</td>
                      <td className="px-4 py-3 text-[#C5A059]">{a.reference}</td>
                      <td className="px-4 py-3 text-[#A1A1AA]">{a.email}<br />{a.telephone}</td>
                      <td className="px-4 py-3 text-[#A1A1AA]">{montantLabel(a.montant_frais)}</td>
                      <td className="px-4 py-3 text-[#A1A1AA]">{(a.archived_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3 text-[#71717A] text-xs">{a.archive_par || "—"}</td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => restaurer(a.id)}
                          data-testid={`archive-restore-${a.id.slice(0, 8)}`}
                          className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 border border-[#C5A059]/60 text-[#C5A059] hover:bg-[#C5A059] hover:text-black transition-colors"
                        >
                          <RotateCcw size={12} /> Restaurer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
